import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import { useDeferredNavigate } from '#/hooks/useDeferredNavigate';
import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useEmbedSearchWidget } from '#/modules/embedSearchWidget/contexts/EmbedSearchWidgetContext';
import {
  useSearchFiltersByName,
  useSearchGetters,
} from '#/modules/search/contexts/SearchContext';
import { useSearchAnalytics } from '#/modules/search/hooks/useSearchAnalytics';
import { searchGetRoutePathSwitch } from '#/modules/search/routing/helpers/searchGetRoutePathSwitch';
import type { SearchFiltersByName } from '#/modules/search/types/FiltersByName/SearchFiltersByName';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import type { AreaOfFocus } from '#/utils/constants/general/areasOfFocus';

export function useSearchFacetToggleOption(
  facetType: keyof SearchFiltersByName,
) {
  const { locale } = useI18n();
  const { embedSearchWidget } = useEmbedSearchWidget();
  const [searchParams] = useSearchParams();
  const searchFiltersByName = useSearchFiltersByName();
  const searchParamsRef = useLatest<URLSearchParams>(searchParams);

  const navigate = useDeferredNavigate();

  const { getSearchRadius } = useSearchGetters();
  const { getSearchTrackingData } = useSearchAnalytics();

  const toggle = useCallback(
    (name: AreaOfFocus) => {
      const searchFacetSelectedValues = searchFiltersByName
        ? (searchFiltersByName[facetType] ?? [])
        : [];

      const filterActiveWhenSelecting =
        searchFacetSelectedValues.includes(name);

      if (filterActiveWhenSelecting) {
        searchParamsRef.current.delete(facetType, name);
        document.querySelector<HTMLInputElement>(
          `[data-facet-type=${facetType}] input[name=${name}]`,
        )!.checked = false;
      } else {
        searchParamsRef.current.append(facetType, name);
        document.querySelector<HTMLInputElement>(
          `[data-facet-type=${facetType}] input[name=${name}]`,
        )!.checked = true;
      }

      // Toggling a filter should always reset results to the first page
      if (searchParamsRef.current.has('page')) {
        searchParamsRef.current.delete('page');
      }

      let newSearchParams;
      const searchRadiusFromState = getSearchRadius();

      if (filterActiveWhenSelecting) {
        newSearchParams = {
          ...searchFiltersByName,
          [facetType]: Array.isArray(searchFacetSelectedValues)
            ? searchFacetSelectedValues.filter((value) => value !== name)
            : undefined,
        };
      } else {
        newSearchParams = {
          ...searchFiltersByName,
          [facetType]: Array.isArray(searchFacetSelectedValues)
            ? [...searchFacetSelectedValues, name]
            : [searchFacetSelectedValues, name],
        };
      }

      const newRoute = searchGetRoutePathSwitch({
        locale,
        q: searchParamsRef.current.get('q') ?? '',
        radius: searchParamsRef.current.get('radius') ?? searchRadiusFromState,
        filtersByName: {
          ...newSearchParams,
        },
        embedSearchWidgetId: embedSearchWidget?.id ?? null,
      });

      void navigate(newRoute, {
        replace: true,
      });

      trackEvent('Changed Search Filter', {
        filter_name: facetType,
        [filterActiveWhenSelecting ? 'deselected_value' : 'selected_value']:
          name,
        ...getSearchTrackingData(),
      });
    },
    [
      locale,
      embedSearchWidget?.id,
      facetType,
      getSearchRadius,
      getSearchTrackingData,
      navigate,
      searchFiltersByName,
      searchParamsRef,
    ],
  );

  return toggle;
}
